import { useState } from 'react'
import { Pencil, Trash2 } from 'lucide-react'
import type { NtExemplar } from '../../core/types'
import { PROJECT_TYPES, useExemplars, saveExemplar, deleteExemplar, type ExemplarContent } from './model'
import { Text, Area } from '../../ui/Field'
import { Empty, ErrorState } from '../../ui/States'

/**
 * Setup → Project Exemplars: what a strong independent-project proposal looks
 * like, and what one that gets sent back looks like, side by side.
 *
 * Students see these next to their own proposal, and seminar teachers and
 * admins next to the queue. The "why" is the teaching — every weak proposal
 * fails the same few ways, and naming the way is faster than any rubric.
 *
 * Use last year's real ones where you can, with the student's name and
 * anything identifying taken out: these are read by every student.
 */
export function Exemplars({ who }: { who: string }) {
  const rows = useExemplars()
  const [editing, setEditing] = useState<NtExemplar | 'new' | null>(null)

  if (editing) {
    return <ExemplarForm initial={editing === 'new' ? undefined : editing} who={who} onDone={() => setEditing(null)} />
  }

  const strong = rows.filter((e) => e.good)
  const weak = rows.filter((e) => !e.good)

  return (
    <>
      <div className="notice notice--info" style={{ marginTop: 'var(--s-2)' }}>
        <span aria-hidden="true">i</span>
        <div>
          Every student reads these next to their own proposal. Real examples from past years teach best:
          take out the student’s name and anything that identifies them first.
        </div>
      </div>
      <button className="btn" style={{ width: 'auto' }} onClick={() => setEditing('new')}>Add an example</button>

      {rows.length === 0 && (
        <Empty title="No examples yet" body="One strong proposal and one that would be sent back is enough to start." />
      )}
      {[['Strong', strong], ['Would be sent back', weak]].map(([label, list]) =>
        (list as NtExemplar[]).length > 0 && (
          <div key={label as string}>
            <h2 className="work__heading">{label as string} <span className="work__badge">{(list as NtExemplar[]).length}</span></h2>
            <ul className="work">
              {(list as NtExemplar[]).map((e) => <Row key={e.id} e={e} onEdit={() => setEditing(e)} />)}
            </ul>
          </div>
        ))}
    </>
  )
}

function Row({ e, onEdit }: { e: NtExemplar; onEdit: () => void }) {
  const [confirming, setConfirming] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  return (
    <li className="work__row" style={{ alignItems: 'flex-start' }}>
      <div className="work__main">
        <div style={{ fontWeight: 610 }}>{e.title}</div>
        <div className="work__sub">{PROJECT_TYPES.find((t) => t.k === e.type)?.label ?? e.type}</div>
        <div className="work__status" style={{ display: 'block' }}><strong>Why:</strong> {e.why || '—'}</div>
        {err && <ErrorState body={err} />}
        {confirming && (
          <div className="opt__bar" style={{ marginTop: 'var(--s-2)' }}>
            <button className="btn" style={{ width: 'auto' }}
              onClick={() => deleteExemplar(e.id).catch((x) => setErr(String(x)))}>Yes, delete it</button>
            <button className="btn btn--ghost" style={{ width: 'auto' }} onClick={() => setConfirming(false)}>Keep it</button>
          </div>
        )}
      </div>
      <button className="opt__icon" aria-label={`Edit ${e.title}`} onClick={onEdit}><Pencil size={15} aria-hidden="true" /></button>
      <button className="opt__icon opt__icon--danger" aria-label={`Delete ${e.title}`} onClick={() => setConfirming(true)}>
        <Trash2 size={15} aria-hidden="true" />
      </button>
    </li>
  )
}

function ExemplarForm({ initial, who, onDone }: { initial?: NtExemplar; who: string; onDone: () => void }) {
  const [f, setF] = useState<ExemplarContent>({
    type: initial?.type ?? 'shadow', good: initial?.good ?? true,
    title: initial?.title ?? '', why: initial?.why ?? '', detail: initial?.detail ?? '',
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const set = <K extends keyof ExemplarContent>(k: K, v: ExemplarContent[K]) => setF((x) => ({ ...x, [k]: v }))

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setErr(null)
    try {
      await saveExemplar(initial?.id, { ...f, title: f.title.trim(), why: f.why.trim(), detail: f.detail.trim() }, who)
      onDone()
    } catch (x) { setErr(x instanceof Error ? x.message : String(x)); setBusy(false) }
  }

  return (
    <form onSubmit={save} className="card" style={{ marginTop: 'var(--s-2)' }}>
      <button type="button" className="backlink" onClick={onDone}
        style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0 }}>← Examples</button>
      <h2 style={{ fontSize: 'var(--t-md)', margin: '0 0 var(--s-3)' }}>{initial ? 'Edit example' : 'Add an example'}</h2>
      {err && <ErrorState body={`Not saved. ${err}`} />}

      <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--s-4)' }}>
        <legend style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink-2)', padding: 0 }}>This example is</legend>
        <div style={{ display: 'grid', gap: 4, marginTop: 'var(--s-2)' }}>
          {[[true, 'Strong: what a good proposal looks like'], [false, 'Weak: one that would be sent back']].map(([v, label]) => (
            <label key={String(v)} className="pick">
              <input type="radio" name="good" checked={f.good === v} onChange={() => set('good', v as boolean)} />
              <span className="pick__label">{label as string}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: '0 0 var(--s-4)' }}>
        <legend style={{ fontSize: 'var(--t-sm)', fontWeight: 600, color: 'var(--ink-2)', padding: 0 }}>Kind of project</legend>
        <div style={{ display: 'grid', gap: 4, marginTop: 'var(--s-2)' }}>
          {PROJECT_TYPES.map((t) => (
            <label key={t.k} className="pick">
              <input type="radio" name="etype" checked={f.type === t.k} onChange={() => set('type', t.k)} />
              <span className="pick__label">{t.label}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <Text label="Title" value={f.title} onChange={(v) => set('title', v)} required
        placeholder="Four days with a large-animal vet" />
      <Area label={f.good ? 'Why it works' : 'Why it would be sent back'} value={f.why} onChange={(v) => set('why', v)} required
        placeholder={f.good ? 'A named host who has agreed, a plan that changes each day, something to show Friday.'
          : 'No host, no plan for each day, nothing that exists Friday that does not exist now.'} />
      <Area label="The proposal itself" value={f.detail} onChange={(v) => set('detail', v)}
        placeholder="What the student wrote, with anything identifying taken out." />

      <button className="btn" type="submit" disabled={busy || !f.title.trim() || !f.why.trim()}>
        {busy ? 'Saving…' : 'Save'}
      </button>
    </form>
  )
}
